import { useCallback, useEffect, useRef, useState } from "react";
import { useBackend } from "../../lib/backend";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import {
  riskFlowHeadlineNotificationDetail,
  shouldDeliverRiskFlowHeadlineCard,
} from "../../lib/riskflow-notification-policy";
import { useRiskFlowAlertSurfaceActive } from "../../lib/riskflow-alert-surface";
import type { WatchlistPhrase } from "../../lib/services/riskflow";

export function useRiskFlowArrivals(
  alerts: RiskFlowAlert[],
  scopeKey: string,
): string | null {
  const backend = useBackend();
  const isTradingSurfaceActive = useRiskFlowAlertSurfaceActive();
  const [freshAlertId, setFreshAlertId] = useState<string | null>(null);
  const [watchPhrases, setWatchPhrases] = useState<WatchlistPhrase[]>([]);
  const previousIdsRef = useRef<Set<string>>(new Set());
  const notifiedIdsRef = useRef<Set<string>>(new Set());
  const scopeRef = useRef(scopeKey);
  const skipNextBatchRef = useRef(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    previousIdsRef.current = new Set();
    notifiedIdsRef.current = new Set();
    scopeRef.current = scopeKey;
    skipNextBatchRef.current = true;
    setFreshAlertId(null);
  }, [scopeKey]);

  useEffect(() => {
    const currentIds = new Set(alerts.map((alert) => alert.id));
    if (scopeRef.current !== scopeKey || skipNextBatchRef.current) {
      scopeRef.current = scopeKey;
      skipNextBatchRef.current = false;
      previousIdsRef.current = currentIds;
      return;
    }
    const previousIds = previousIdsRef.current;
    if (previousIds.size === 0) {
      previousIdsRef.current = currentIds;
      return;
    }

    const newcomers = alerts
      .filter((alert) => !previousIds.has(alert.id))
      .sort(
        (left, right) =>
          (Date.parse(right.publishedAt) || 0) -
          (Date.parse(left.publishedAt) || 0),
      );
    previousIdsRef.current = currentIds;
    const latest = newcomers[0];
    if (!latest) return;

    setFreshAlertId(latest.id);
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => setFreshAlertId(null), 1200);

    for (const alert of newcomers) {
      if (
        notifiedIdsRef.current.has(alert.id) ||
        !shouldDeliverRiskFlowHeadlineCard(alert, isTradingSurfaceActive)
      )
        continue;
      const watchPhrase = watchPhrases.find((phrase) =>
        watchPhraseMatches(phrase, alert.headline, alert.tags ?? []),
      );
      notifiedIdsRef.current.add(alert.id);
      window.dispatchEvent(
        new CustomEvent("fintheon:riskflow-card-notification", {
          detail: riskFlowHeadlineNotificationDetail(
            alert,
            watchPhrase?.phrase,
          ),
        }),
      );
    }
  }, [alerts, isTradingSurfaceActive, scopeKey, watchPhrases]);

  const refreshWatchPhrases = useCallback(async () => {
    try {
      const result = await backend.riskflow.getPhrases();
      setWatchPhrases(result.phrases ?? []);
    } catch {
      setWatchPhrases([]);
    }
  }, [backend.riskflow]);

  useEffect(() => {
    void refreshWatchPhrases();
    const refresh = () => void refreshWatchPhrases();
    window.addEventListener("fintheon:watchtags-refetch", refresh);
    return () =>
      window.removeEventListener("fintheon:watchtags-refetch", refresh);
  }, [refreshWatchPhrases]);

  useEffect(
    () => () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    [],
  );

  return freshAlertId;
}

function watchPhraseMatches(
  phrase: WatchlistPhrase,
  headline: string,
  tags: string[],
): boolean {
  const target = [headline, ...tags].join(" ").toLowerCase();
  if (phrase.matchType === "exact") {
    return target.includes(phrase.phraseLower);
  }
  return phrase.phraseLower
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => target.includes(word));
}
